/**
 * An admin viewing the reader as another user (`?view_as=<user id>`, PRO-1253).
 *
 * Opening any page with `?view_as=<user id>` makes this tab view as that
 * reader until it stops (the banner's "Stop viewing"): every API call carries
 * `X-View-As`, so the server answers as that reader would be answered (their
 * feeds, their For You, their hides). The tab remembers it (sessionStorage),
 * since the reader's own links drop the query. Viewing is read-only: the server
 * refuses every write that carries the header, and the page sends no telemetry.
 * The session's own identity (`/api/me` for the user menu) stays the admin's.
 */

export const VIEW_AS_HEADER = 'X-View-As';
export const VIEW_AS_PARAM = 'view_as';
const STORAGE_KEY = 'kf_view_as';
const USER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const INVALID_USER_ID = 'invalid-view-as';

/** The requested id, an invalid marker, or the tab's remembered id; null when not viewing. */
export function viewAsFrom(search: string, remembered: string | null): string | null {
  const asked = new URLSearchParams(search).get(VIEW_AS_PARAM);
  if (asked !== null) {
    const id = asked.trim().toLowerCase();
    return USER_ID.test(id) ? id : INVALID_USER_ID;
  }
  if (remembered === INVALID_USER_ID) return remembered;
  return remembered && USER_ID.test(remembered) ? remembered : null;
}

function remembered(): string | null {
  try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
}

/** The user id this tab views as, or null. A `?view_as=` in the address is remembered for the tab. */
export function viewAsUserId(): string | null {
  if (typeof window === 'undefined') return null;
  const viewed = viewAsFrom(window.location.search, remembered());
  if (viewed) {
    try { sessionStorage.setItem(STORAGE_KEY, viewed); } catch { /* still views as them on this page */ }
  }
  return viewed;
}

/** Stop viewing as another reader in this tab. */
export function stopViewingAs(): void {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* nothing remembered */ }
}

/** *search* without `view_as`, as a query string ('' or '?…'). */
export function withoutViewAs(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(VIEW_AS_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

/** *init* carrying `X-View-As` when this tab views as another reader. */
export function withViewAs(init: RequestInit, viewed: string | null = viewAsUserId()): RequestInit {
  if (!viewed) return init;
  const headers = new Headers(init.headers || {});
  headers.set(VIEW_AS_HEADER, viewed);
  return { ...init, headers };
}

/** Where an admin opens *userId*'s For You as them. A full page load, so nothing the admin's tab held carries over. */
export function viewAsHref(userId: string, path = '/'): string {
  return `${path}?${new URLSearchParams({ [VIEW_AS_PARAM]: userId })}`;
}
